/*!
 * egt.people.group — repeatable decision-maker group, capped (app-scoped).
 *
 * Spec §2: "If no, who else is involved in approvals? [name][email][phone]
 * [title] — allow adding up to 2."
 *
 * An "Add" button (disabled at `max`) appends a row of name / email / phone /
 * title inputs; each row has a remove button. Rendered as inline raw DOM
 * (NOT nested <uae>) so the wizard sees one uae[name] boundary and serializes
 * via this save(). These emails are PLAIN inputs — they are deliberately NOT
 * routed through the email-verification flow (spec: do NOT verify decision-
 * maker emails).
 *
 * save() -> array of { name, email, phone, title } (only non-empty rows)
 * load() -> rebuilds rows from an array.
 */

var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.people.group",
  title: "Decision-Maker Group",
  description: "Add up to a maximum number of additional decision-makers (name, email, phone, title).",
  control: true,
  preview: "egt.preview.people",
  supportServer: false,
  fields: {
    name: {
      title: "Name",
      type: "string",
      description: "Form field name.",
      required: true
    },
    title: {
      title: "Title",
      type: "string",
      description: "Control title."
    },
    description: {
      title: "Description",
      type: "inner",
      description: "Helper text."
    },
    max: {
      title: "Max",
      type: "number",
      description: "Maximum number of people that can be added.",
      default: 2
    },
    value: {
      title: "Value",
      type: "any",
      description: "Initial array of { name, email, phone, title }."
    }
  }
});

function resolveMax(value) {
  var n = Number(value);
  if (!Number.isFinite(n) || n < 1) return 2;
  return Math.floor(n);
}

function normalizePeople(value) {
  var rows = value instanceof Array ? value : [];
  var out = [];
  for (var i = 0; i < rows.length; i++) {
    var row = rows[i] || {};
    out.push({
      name: (row.name || "").toString(),
      email: (row.email || "").toString(),
      phone: (row.phone || "").toString(),
      title: (row.title || "").toString()
    });
  }
  return out;
}

function buildRow(person) {
  person = person || {};
  var row = document.createElement("div");
  row.className = "egt-people__row border rounded p-3 mb-2";
  row.setAttribute("data-people-row", "true");

  function field(colClass, labelText, inputType, attrName, val, placeholder) {
    var col = document.createElement("div");
    col.className = colClass;
    var l = document.createElement("label");
    l.className = "form-label small mb-1";
    l.textContent = labelText;
    var input = document.createElement("input");
    input.type = inputType;
    input.className = "form-control form-control-sm";
    input.setAttribute(attrName, "true");
    if (placeholder) input.placeholder = placeholder;
    if (val) input.value = val;
    col.appendChild(l);
    col.appendChild(input);
    return col;
  }

  var grid = document.createElement("div");
  grid.className = "row g-2";
  grid.appendChild(field("col-12 col-lg-6", "Name", "text", "data-people-name", person.name, "Full name"));
  grid.appendChild(field("col-12 col-lg-6", "Title / Role", "text", "data-people-title", person.title, "e.g. CFO"));
  grid.appendChild(field("col-12 col-lg-6", "Email", "email", "data-people-email", person.email, "name@example.com"));
  grid.appendChild(field("col-12 col-lg-6", "Phone", "tel", "data-people-phone", person.phone, "Optional"));
  row.appendChild(grid);

  var removeWrap = document.createElement("div");
  removeWrap.className = "text-end mt-2";
  var removeBtn = document.createElement("button");
  removeBtn.type = "button";
  removeBtn.className = "btn btn-sm btn-outline-secondary egt-people__remove";
  removeBtn.setAttribute("data-people-remove", "true");
  removeBtn.innerHTML = "<i class='bi bi-x-lg'></i><span class='ms-1'>Remove</span>";
  removeWrap.appendChild(removeBtn);
  row.appendChild(removeWrap);

  return row;
}

exports.render = async function (options) {
  var name = (options.name || "").toString().trim();
  var title = (options.title || "").toString().trim();
  var description = (options.description || "").toString().trim();
  var max = resolveMax(options.max);
  var initial = normalizePeople(options.value);

  var wrapper = document.createElement("div");
  wrapper.className = "form-group pb-3 egt-people";
  wrapper.setAttribute("name", name);
  wrapper.setAttribute("data-max", String(max));

  if (title) {
    var label = document.createElement("label");
    label.className = "form-label fw-semibold";
    label.textContent = title;
    wrapper.appendChild(label);
  }

  var help = document.createElement("div");
  help.className = "form-text mb-2";
  help.textContent = description ? (description + " (Add up to " + max + ".)") : ("Add up to " + max + ".");
  wrapper.appendChild(help);

  var rowsHost = document.createElement("div");
  rowsHost.className = "egt-people__rows";
  rowsHost.setAttribute("data-people-rows", "true");
  wrapper.appendChild(rowsHost);

  for (var i = 0; i < initial.length && i < max; i++) {
    rowsHost.appendChild(buildRow(initial[i]));
  }

  var addBtn = document.createElement("button");
  addBtn.type = "button";
  addBtn.className = "btn btn-sm btn-outline-secondary egt-people__add";
  addBtn.setAttribute("data-people-add", "true");
  addBtn.innerHTML = "<i class='bi bi-plus-lg'></i><span class='ms-1'>Add decision-maker</span>";
  wrapper.appendChild(addBtn);

  function rowCount() {
    return rowsHost.querySelectorAll("[data-people-row='true']").length;
  }

  function syncAdd() {
    addBtn.disabled = rowCount() >= max;
  }

  addBtn.addEventListener("click", function () {
    if (rowCount() >= max) return;
    rowsHost.appendChild(buildRow({}));
    syncAdd();
  });

  rowsHost.addEventListener("click", function (event) {
    var remove = event.target.closest("[data-people-remove='true']");
    if (!remove) return;
    var row = remove.closest("[data-people-row='true']");
    if (row) row.remove();
    syncAdd();
  });

  syncAdd();
  return wrapper;
};

function readRow(row) {
  function val(attr) {
    var el = row.querySelector("[" + attr + "]");
    return el ? (el.value || "").toString().trim() : "";
  }
  return {
    name: val("data-people-name"),
    title: val("data-people-title"),
    email: val("data-people-email"),
    phone: val("data-people-phone")
  };
}

async function save(name, control, repo) {
  var rows = control.querySelectorAll("[data-people-row='true']");
  var people = [];
  var errors = [];

  for (var i = 0; i < rows.length; i++) {
    var person = readRow(rows[i]);
    var hasAny = person.name || person.email || person.phone || person.title;
    if (!hasAny) continue;
    // A populated decision-maker should at least have a name.
    if (!person.name) {
      errors.push({ type: "validation", message: "Each decision-maker needs a name." });
    }
    people.push(person);
  }

  if (errors.length > 0) {
    repo.success = false;
    repo.errors = errors;
    control.classList.add("is-invalid");
    repo.data = people;
    return repo;
  }

  control.classList.remove("is-invalid");
  repo.success = true;
  repo.data = people;
  return repo;
}
module.exports.save = save;

async function load(name, control, data) {
  var max = resolveMax(control.getAttribute("data-max"));
  var people = normalizePeople(data);
  var rowsHost = control.querySelector("[data-people-rows='true']");
  var addBtn = control.querySelector("[data-people-add='true']");
  if (!rowsHost) return;
  rowsHost.innerHTML = "";
  for (var i = 0; i < people.length && i < max; i++) {
    rowsHost.appendChild(buildRow(people[i]));
  }
  if (addBtn) addBtn.disabled = rowsHost.querySelectorAll("[data-people-row='true']").length >= max;
}
module.exports.load = load;
